import React, { useState } from 'react';

import { BarChart, CheckCircle, ChevronDown, ChevronUp, FileText, Target } from 'lucide-react';

const ImplementationProcess = () => {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const toggleStep = (stepNumber: number) => {
    setExpandedStep(expandedStep === stepNumber ? null : stepNumber);
  };
  const steps = [
    {
      number: 1,
      title: 'Diagnóstico Inicial',
      description: 'Realizar una evaluación completa de necesidades y objetivos específicos',
      icon: <CheckCircle className="h-6 w-6" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      hoverEffect: 'hover:bg-blue-100 transition-colors duration-300',
      details: [
        'Análisis exhaustivo de la infraestructura actual',
        'Identificación de puntos de mejora y oportunidades',
        'Evaluación de recursos necesarios',
        'Definición de objetivos medibles',
      ],
    },
    {
      number: 2,
      title: 'Planificación Estratégica',
      description: 'Desarrollar un plan de acción detallado y cronograma',
      icon: <FileText className="h-6 w-6" />,
      color: 'text-[#003366]',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-300',
      hoverEffect: 'hover:bg-blue-200 transition-colors duration-300',
      details: [
        'Definición de alcance y objetivos del proyecto',
        'Asignación de recursos y responsabilidades',
        'Establecimiento de plazos y entregables',
        'Análisis de riesgos y plan de contingencia',
      ],
    },
    {
      number: 3,
      title: 'Implementación',
      description: 'Ejecutar el proyecto utilizando metodologías ágiles',
      icon: <Target className="h-6 w-6" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      hoverEffect: 'hover:bg-blue-100 transition-colors duration-300',
      details: [
        'Desarrollo iterativo con entregas incrementales',
        'Pruebas continuas y control de calidad',
        'Despliegue progresivo de funcionalidades',
        'Capacitación y transferencia de conocimiento',
      ],
    },
    {
      number: 4,
      title: 'Seguimiento y Optimización',
      description: 'Monitoreo continuo y ajustes para resultados óptimos',
      icon: <BarChart className="h-6 w-6" />,
      color: 'text-[#003366]',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-300',
      hoverEffect: 'hover:bg-blue-200 transition-colors duration-300',
      details: [
        'Monitoreo de KPIs y métricas de rendimiento',
        'Análisis de resultados y retroalimentación',
        'Implementación de mejoras continuas',
        'Evaluación periódica de la satisfacción del cliente',
      ],
    },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Nuestro Proceso de Implementación
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Metodología probada para garantizar resultados exitosos en cada proyecto
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          {/* Vertical Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-100 via-blue-200 to-blue-300"></div>

          {/* Steps */}
          <div className="space-y-12">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className={`flex items-center ${index % 2 === 0 ? '' : 'flex-row-reverse'}`}
              >
                <div
                  className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>

                <div className="relative z-10">
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-full ${step.bgColor} ${step.borderColor} border-2 shadow-md transform transition-transform duration-300 hover:scale-110`}
                  >
                    <span className={`${step.color} font-bold text-lg`}>{step.number}</span>
                  </div>
                </div>

                <div className={`w-1/2 ${index % 2 === 0 ? 'pl-12' : 'pr-12'}`}>
                  <div
                    className={`p-4 rounded-lg shadow-md ${step.bgColor} border ${step.borderColor} ${step.hoverEffect} min-h-[80px] cursor-pointer transition-all duration-300`}
                    onClick={() => toggleStep(step.number)}
                  >
                    <div className="flex items-center w-full justify-between">
                      <div className="flex items-center">
                        <div className={`mr-3 ${step.color}`}>{step.icon}</div>
                        <div className="font-medium text-gray-800">Fase {step.number}</div>
                      </div>
                      <div className="text-gray-600">
                        {expandedStep === step.number ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </div>
                    </div>

                    {expandedStep === step.number && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <ul className="space-y-2">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start">
                              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-2 mt-2"></div>
                              <span className="text-sm text-gray-700">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImplementationProcess;
